export interface FieldConfig {
  name: string;
  label: string;
  type: "text" | "file" | "select" | "date";
  required: boolean;
  placeholder?: string;
  multiline?: boolean;
  hint?: string;
  options?: { label: string; value: string }[];
}

const textFields: Record<string, FieldConfig> = {
  rcNumber: {
    name: "rcNumber",
    label: "RC Number",
    type: "text",
    required: true,
    placeholder: "Enter RC number",
  },
  tinNumber: {
    name: "tinNumber",
    label: "TIN Number",
    type: "text",
    required: true,
    placeholder: "Enter TIN number",
  },
  natureOfBusiness: {
    name: "natureOfBusiness",
    label: "Nature of Business",
    type: "text",
    required: true,
    placeholder: "Describe your business",
    multiline: true,
  },
  bnNumber: {
    name: "bnNumber",
    label: "BN Number",
    type: "text",
    required: true,
    placeholder: "Enter BN number",
  },
  coaNumber: {
    name: "coaNumber",
    label: "COA Number",
    type: "text",
    required: false,
    placeholder: "Enter COA number",
  },
  stateIdNumber: {
    name: "stateIdNumber",
    label: "State ID Number",
    type: "text",
    required: true,
    placeholder: "Enter State ID",
  },
  registrationNumber: {
    name: "registrationNumber",
    label: "Registration Number",
    type: "text",
    required: true,
    placeholder: "Enter registration number",
  },
  typeOfEnterprise: {
    name: "typeOfEnterprise",
    label: "Type of Enterprise",
    type: "text",
    required: true,
    placeholder: "Enter enterprise type",
  },
  businessRegistrationNumber: {
    name: "businessRegistrationNumber",
    label: "Business Registration Number",
    type: "text",
    required: false,
    placeholder: "Enter business registration number (optional)",
  },
  countryOfIncorporation: {
    name: "countryOfIncorporation",
    label: "Country of Incorporation",
    type: "text",
    required: true,
    placeholder: "Enter country",
  },
  localRepresentativeContactInfo: {
    name: "localRepresentativeContactInfo",
    label: "Local Representative Contact",
    type: "text",
    required: true,
    placeholder: "Enter contact information",
  },
  numberOfEmployees: {
    name: "numberOfEmployees",
    label: "Number of Employees",
    type: "select",
    required: true,
    placeholder: "Select number of employees",
    options: [
      { label: "1–10", value: "1-10" },
      { label: "11–50", value: "11-50" },
      { label: "51–100", value: "51-100" },
      { label: "101–250", value: "101-250" },
      { label: "251–500", value: "251-500" },
      { label: "500+", value: "500+" },
    ],
  },
  organizationName: {
    name: "organizationName",
    label: "Organization Name",
    type: "text",
    required: true,
    placeholder: "Enter organization name",
  },
  countryOfRepresentation: {
    name: "countryOfRepresentation",
    label: "Country of Representation",
    type: "text",
    required: true,
    placeholder: "Enter country of representation",
  },
  countryOfOrigin: {
    name: "countryOfOrigin",
    label: "Country of Origin",
    type: "text",
    required: true,
    placeholder: "Enter country of origin",
  },
  natureOfOperations: {
    name: "natureOfOperations",
    label: "Nature of Operations",
    type: "text",
    required: true,
    placeholder: "Describe operations",
  },
  ngoRegistrationNumber: {
    name: "ngoRegistrationNumber",
    label: "NGO Registration Number",
    type: "text",
    required: true,
    placeholder: "Enter NGO registration number",
  },
  incorporationDate: {
    name: "incorporationDate",
    label: "Incorporation Date",
    type: "date",
    required: true,
    placeholder: "Select incorporation date",
  },
  localContactAddress: {
    name: "localContactAddress",
    label: "Local Contact Address",
    type: "text",
    required: true,
    placeholder: "Enter local contact address",
  },
  embassyMissionName: {
    name: "embassyMissionName",
    label: "Embassy / Mission Name",
    type: "text",
    required: true,
    placeholder: "Enter embassy or mission name",
  },
  contactEmailAddress: {
    name: "contactEmailAddress",
    label: "Contact Email Address",
    type: "text",
    required: true,
    placeholder: "Enter contact email address",
  },
  employerName: {
    name: "employerName",
    label: "Employer Name",
    type: "text",
    required: true,
    placeholder: "Enter employer name",
  },
  employerInformation: {
    name: "employerInformation",
    label: "Employer Information",
    type: "text",
    required: true,
    placeholder: "Enter employer information",
    multiline: true,
  },

  currentValidAddress: {
    name: "currentValidAddress",
    label: "Organization Address",
    type: "text",
    required: true,
    placeholder: "Enter organization address",
    multiline: true,
  },
  companyRegistrationNumber: {
    name: "companyRegistrationNumber",
    label: "Company Registration Number",
    type: "text",
    required: true,
    placeholder: "Enter company registration number",
  },

  mdaName: {
    name: "mdaName",
    label: "MDA Name",
    type: "text",
    required: true,
    placeholder: "Enter MDA name",
  },
  stateName: {
    name: "stateName",
    label: "State Name",
    type: "text",
    required: true,
    placeholder: "Enter state name",
  },
};

const fileFields: Record<string, FieldConfig> = {
  cacDocumentUrl: {
    name: "cacDocumentUrl",
    label: "CAC Document",
    type: "file",
    required: true,
  },
  tinDocumentUrl: {
    name: "tinDocumentUrl",
    label: "TIN Document",
    type: "file",
    required: true,
  },
  letterRequestingEmployerCode: {
    name: "letterRequestingEmployerCode",
    label: "Letter Requesting Employer Code",
    type: "file",
    required: true,
  },
  registrationCertificateUrl: {
    name: "registrationCertificateUrl",
    label: "Registration Certificate",
    type: "file",
    required: true,
  },
  letterheadDocumentUrl: {
    name: "letterheadDocumentUrl",
    label: "Official letter on MDA letterhead",
    type: "file",
    required: true,
    hint: "Letter must reference COA number from OAGF",
  },
  cacBnCertificateUrl: {
    name: "cacBnCertificateUrl",
    label: "CAC/BN Certificate",
    type: "file",
    required: true,
  },
  foreignBusinessRegistrationDocUrl: {
    name: "foreignBusinessRegistrationDocUrl",
    label: "Foreign business registration document",
    type: "file",
    required: true,
  },
  diplomaticAuthorizationDocUrl: {
    name: "diplomaticAuthorizationDocUrl",
    label: "Diplomatic authorization or introduction letter",
    type: "file",
    required: true,
  },
  authorizationDocumentUrl: {
    name: "authorizationDocumentUrl",
    label: "Official authorization or registration document",
    type: "file",
    required: true,
  },
  cacCertificateUrl: {
    name: "cacCertificateUrl",
    label: "CAC Certificate of Registration",
    type: "file",
    required: true,
  },
  trusteeshiptDocUrl: {
    name: "trusteeshiptDocUrl",
    label: "Trusteeship documents (where applicable)",
    type: "file",
    required: false,
  },
  businessRegistrationDocUrl: {
    name: "businessRegistrationDocUrl",
    label: "Proof of business registration",
    type: "file",
    required: true,
  },
  identificationDocumentUrl: {
    name: "identificationDocumentUrl",
    label: "Identification document of employer or authorized signatory",
    type: "file",
    required: true,
  },
  certificateOfRegistration: {
    name: "certificateOfRegistration",
    label: "Certificate of Registration",
    type: "file",
    required: true,
  },
  evidenceOfIdentificationNumber: {
    name: "evidenceOfIdentificationNumber",
    label: "Evidence of Identification Number",
    type: "file",
    required: true,
  },
  cacCertificate: {
    name: "cacCertificate",
    label: "CAC Certificate",
    type: "file",
    required: true,
  },
  officialLetterOnStateGovernmentMdaLetterhead: {
    name: "officialLetterOnStateGovernmentMdaLetterhead",
    label:
      "Official letter on State Government MDA letterhead requesting employer code",
    type: "file",
    required: true,
  },
  localAuthorizationDocUrl: {
    name: "localAuthorizationDocUrl",
    label: "Local authorization or representative letter (if applicable)",
    type: "file",
    required: false,
  },
};

// Organization type configuration mapping
const orgTypeConfig: Record<string, { fields: string[]; documents: string[] }> =
  {
    PR: {
      fields: ["tinNumber"],
      documents: ["cacCertificate"],
    },
    BR: {
      fields: ["employerName", "natureOfBusiness", "tinNumber"],
      documents: [
        "certificateOfRegistration",
        "evidenceOfIdentificationNumber",
      ],
    },
    PU: {
      fields: ["employerName", "employerInformation", "coaNumber"],
      documents: ["letterheadDocumentUrl"],
    },
    ST: {
      fields: ["mdaName", "stateName", "coaNumber", "numberOfEmployees"],
      documents: ["officialLetterOnStateGovernmentMdaLetterhead"],
    },
    EM: {
      fields: [
        "embassyMissionName",
        "countryOfRepresentation",
        "contactEmailAddress",
        "numberOfEmployees",
      ],
      documents: ["diplomaticAuthorizationDocUrl"],
    },
    FR: {
      fields: [
        "organizationName",
        "countryOfOrigin",
        "natureOfOperations",
        "numberOfEmployees",
      ],
      documents: ["authorizationDocumentUrl"],
    },
    NG: {
      fields: [
        "ngoRegistrationNumber",
        "natureOfBusiness",
        "incorporationDate",
        "numberOfEmployees",
      ],
      documents: ["cacCertificateUrl", "trusteeshiptDocUrl"],
    },
    UN: {
      fields: ["registrationNumber", "tinNumber", "natureOfBusiness"],
      documents: [
        "registrationCertificateUrl",
        "tinDocumentUrl",
        "letterRequestingEmployerCode",
      ],
    },
    MP: {
      fields: [
        "employerName",
        "natureOfBusiness",
        "numberOfEmployees",
        "incorporationDate",
      ],
      documents: ["businessRegistrationDocUrl", "identificationDocumentUrl"],
    },
    CB: {
      fields: [
        "employerName",
        "countryOfOperation",
        "natureOfBusiness",
        "localContactAddress",
        "numberOfEmployees",
      ],
      documents: [
        "foreignBusinessRegistrationDocUrl",
        "localAuthorizationDocUrl",
      ],
    },
  };

export function getFieldsForOrganizationType(
  organizationTypeCode: string,
  requiredFieldsFromAPI?: string[],
  requiredDocumentsFromAPI?: string[],
): { fields: FieldConfig[]; documents: FieldConfig[] } {
  const fields: FieldConfig[] = [];
  const documents: FieldConfig[] = [];

  // Use API data if provided, otherwise fall back to local config
  const requiredFieldNames =
    requiredFieldsFromAPI || orgTypeConfig[organizationTypeCode]?.fields || [];
  const requiredDocumentNames =
    requiredDocumentsFromAPI ||
    orgTypeConfig[organizationTypeCode]?.documents ||
    [];

  requiredFieldNames.forEach((fieldName) => {
    if (textFields[fieldName]) {
      fields.push(textFields[fieldName]);
    }
  });

  requiredDocumentNames.forEach((docName) => {
    if (fileFields[docName]) {
      documents.push(fileFields[docName]);
    }
  });

  return { fields, documents };
}

export const getOrganizationNumberPrefix = (orgTypeCode: string) => {
  if (orgTypeCode === "PR") return "RC";
  if (orgTypeCode === "BR") return "BN";
  if (orgTypeCode === "NG") return "IT";
  return "";
};

export const getOrganizationNumberLabel = (orgTypeCode: string) => {
  if (orgTypeCode === "BR") return "Organization Number";
  return "RC Number";
};
